import { Divider } from '@eventbrite/eds-divider';
import { Typography } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { LocationInput } from '../../../containers/LocationInput';
import { SearchInput } from '../../../containers/SearchInput';
import { useSearchInputContext } from '../../../context';
import { useRunSearch } from '../../../hooks';
import { DropdownOverlay } from '../DropdownOverlay';
import { SearchButton } from '../SearchButton';
import { SearchDropdown } from '../SearchDropdown';
import styles from './SearchBar.module.scss';

const SearchInputContainer = ({
    setShowOverlay,
    handleSearchButtonClick,
}: {
    setShowOverlay: (value: boolean) => void;
    handleSearchButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
    const [showSearch, setShowSearch] = useState(false);

    const handleSearchClick = useCallback(() => {
        setShowSearch(true);
        setShowOverlay(true);
    }, [setShowSearch, setShowOverlay]);

    const handleSearchClickOutside = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            if (target?.id === 'search-autocomplete-input') {
                return;
            } else if (
                target?.id === 'location-autocomplete' ||
                target?.id === 'search-dropdown-overlay'
            ) {
                setShowSearch(false);
            } else {
                setShowSearch(false);
                setShowOverlay(false);
            }
        },
        [setShowSearch, setShowOverlay],
    );

    return (
        <div
            className={styles.searchLabelContainer}
            data-heap-id="seo-global-nav-search-bar-search-bar-click"
        >
            <div className={styles.searchLabel}>
                <SearchInput
                    variant="header"
                    dropdownVisible={showSearch}
                    handleSearchClick={handleSearchClick}
                    handleBackButtonClick={handleSearchClickOutside}
                />
            </div>
            <div className={styles.mobileSearchBarRightSide}>
                <SearchButton onClick={handleSearchButtonClick} />
            </div>
            {showSearch && (
                <SearchDropdown onClickOutside={handleSearchClickOutside} />
            )}
        </div>
    );
};

const LocationInputContainer = ({
    setShowOverlay,
}: {
    setShowOverlay: (value: boolean) => void;
}) => {
    const handleLocationClick = useCallback(() => {
        setShowOverlay(true);
    }, [setShowOverlay]);

    const handleLocationSelect = () => {
        if (window?.innerWidth >= 660) {
            setShowOverlay(false);
        }
    };

    return (
        <div className={styles.dividerAndLocationContainer}>
            <div className={styles.dividerContainer}>
                <Divider direction="vertical" color="ui-500" />
            </div>
            <div
                className={styles.locationContainer}
                onClick={handleLocationClick}
            >
                <div className={styles.locationLabel}>
                    <LocationInput
                        onLocationSelect={handleLocationSelect}
                        variant="header"
                    />
                </div>
            </div>
        </div>
    );
};

const SearchBar = () => {
    const [showOverlay, setShowOverlay] = useState(false);

    const { query } = useSearchInputContext();
    const handleSearch = useRunSearch();

    const onOverlayClose = useCallback(() => {
        setShowOverlay(false);
    }, [setShowOverlay]);

    const handleSearchButtonClick = useCallback(() => {
        handleSearch({
            search: { q: query || '' },
            analytics: { action: 'RunSearch', label: query },
        });
    }, [handleSearch, query]);

    const searchBarClassnames = classNames(styles.searchBarWithLocation, {
        [styles.overlayOpen]: showOverlay,
    });

    return (
        <>
            <div className={searchBarClassnames} data-testid="header-search">
                <Typography
                    variant="body-md"
                    as="span"
                    color="neutral-700"
                    className={styles.searchBarContent}
                >
                    <SearchInputContainer
                        setShowOverlay={setShowOverlay}
                        handleSearchButtonClick={handleSearchButtonClick}
                    />
                    <LocationInputContainer setShowOverlay={setShowOverlay} />
                    <div className={styles.desktopSearchBarRightSide}>
                        <SearchButton onClick={handleSearchButtonClick} />
                    </div>
                </Typography>
                <DropdownOverlay
                    domNodeId="root"
                    onClose={onOverlayClose}
                    isVisible={showOverlay}
                />
            </div>
        </>
    );
};

export default SearchBar;
